import React from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Icon } from 'design-system-web';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import type { TFunction } from 'i18next';
import type { TemplateSection } from 'inspection-types';
import { S } from 'components/typography';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import { formatSectionTitle } from '../../helpers/inspectionTemplateCustomizeHelpers';

import { Item } from './SectionCardItem';
import { SortableSectionCardItem } from './SortableSectionCardItem';
import styles from './InspectionTemplateCustomizeSectionCard.scss';

const NO_IDS_LOADING = new Set<string>();

type Props = {
  section: TemplateSection;
  expanded: boolean;
  onToggle: () => void;
  /** Server template has no rows for this section — show removed section presentation. */
  isSectionRemovedPresentation?: boolean;
  sectionRemoveDisabled: boolean;
  removeSectionDisabledTitle: string;
  removeItemDisabledTitle: string;
  onRemoveSection?: () => void;
  isRemoveSectionLoading?: boolean;
  /** When set with `isSectionRemovedPresentation`, enables undo (requires template library LD). */
  onRestoreSection?: () => void;
  isRestoreSectionLoading?: boolean;
  /** True while another section/item mutation is in flight (disable restore). */
  restoreSectionDisabled?: boolean;
  restoreSectionDisabledTitle?: string;
  onRemoveItem?: (itemId: string) => void;
  removingItemIds?: ReadonlySet<string>;
  onRestoreItem?: (itemId: string) => void;
  restoringItemIds?: ReadonlySet<string>;
  /** Item IDs shown as removed (strikethrough); defaults to empty. */
  removedOverlayItemIds?: ReadonlySet<string>;
  /** Drag handle element rendered at the start of the section header (provided by parent sortable wrapper). */
  dragHandleNode?: React.ReactNode;
  /** When true, items render with sortable wrappers and react to in-section drag. */
  reorderEnabled?: boolean;
  /** Called when an item is dropped over another item within this section. */
  onReorderItems?: (activeId: string, overId: string) => void;
};

type SectionHeaderRowActionParams = {
  t: TFunction;
  isSectionRemovedPresentation: boolean;
  onRestoreSection?: () => void;
  isRestoreSectionLoading: boolean;
  restoreSectionDisabled: boolean;
  restoreSectionDisabledTitle: string;
  isRemoveSectionLoading: boolean;
  removeSectionDisabled: boolean;
  onRemoveSection?: () => void;
  removeSectionTitle: string;
};

function renderActiveSectionHeaderRowAction({
  t,
  isRemoveSectionLoading,
  removeSectionDisabled,
  onRemoveSection,
  removeSectionTitle,
}: Pick<
  SectionHeaderRowActionParams,
  | 't'
  | 'isRemoveSectionLoading'
  | 'removeSectionDisabled'
  | 'onRemoveSection'
  | 'removeSectionTitle'
>): React.ReactNode {
  if (isRemoveSectionLoading) {
    return (
      <div
        className={styles.rowActionLoadingWrap}
        role="status"
        aria-label={t(
          'web.admin.inspections.templateCustomize.removeSectionLoadingAria'
        )}
        data-test="remove-section-loading"
      >
        <span className={styles.rowActionSpinner} aria-hidden />
      </div>
    );
  }

  return (
    <button
      type="button"
      className={classNames(
        styles.rowActionButton,
        !removeSectionDisabled &&
          onRemoveSection &&
          styles.rowActionButtonInteractive
      )}
      disabled={removeSectionDisabled || !onRemoveSection}
      title={removeSectionTitle}
      aria-label={t(
        'web.admin.inspections.templateCustomize.removeSectionAria'
      )}
      onClick={() => {
        if (!removeSectionDisabled && onRemoveSection) {
          onRemoveSection();
        }
      }}
    >
      <Icon name="trash-alt" set={ICON_SET_FONTAWESOME} type="far" />
    </button>
  );
}

function renderRemovedSectionHeaderRowAction({
  t,
  onRestoreSection,
  isRestoreSectionLoading,
  restoreSectionDisabled,
  restoreSectionDisabledTitle,
}: Pick<
  SectionHeaderRowActionParams,
  | 't'
  | 'onRestoreSection'
  | 'isRestoreSectionLoading'
  | 'restoreSectionDisabled'
  | 'restoreSectionDisabledTitle'
>): React.ReactNode {
  if (!onRestoreSection) {
    return (
      <button
        type="button"
        className={classNames(
          styles.rowActionButton,
          styles.rowActionButtonRestorePlaceholder
        )}
        disabled
        aria-label={t(
          'web.admin.inspections.templateCustomize.revertSectionPlaceholderAria'
        )}
        title={t(
          'web.admin.inspections.templateCustomize.revertSectionPlaceholderTitle'
        )}
      >
        <Icon name="undo" set={ICON_SET_FONTAWESOME} type="far" />
      </button>
    );
  }

  if (isRestoreSectionLoading) {
    return (
      <div
        className={styles.rowActionLoadingWrap}
        role="status"
        aria-label={t(
          'web.admin.inspections.templateCustomize.restoreSectionLoadingAria'
        )}
        data-test="restore-section-loading"
      >
        <span className={styles.rowActionSpinner} aria-hidden />
      </div>
    );
  }

  return (
    <button
      type="button"
      className={classNames(
        styles.rowActionButton,
        !restoreSectionDisabled && styles.rowActionButtonInteractive
      )}
      disabled={restoreSectionDisabled}
      title={restoreSectionDisabledTitle}
      aria-label={t(
        'web.admin.inspections.templateCustomize.restoreSectionAria'
      )}
      onClick={() => {
        if (!restoreSectionDisabled) {
          onRestoreSection();
        }
      }}
    >
      <Icon name="undo" set={ICON_SET_FONTAWESOME} type="far" />
    </button>
  );
}

function renderSectionHeaderRowAction(
  params: SectionHeaderRowActionParams
): React.ReactNode {
  if (!params.isSectionRemovedPresentation) {
    return renderActiveSectionHeaderRowAction(params);
  }

  return renderRemovedSectionHeaderRowAction(params);
}

export function SectionCard({
  section,
  expanded,
  onToggle,
  isSectionRemovedPresentation = false,
  sectionRemoveDisabled,
  removeSectionDisabledTitle,
  removeItemDisabledTitle,
  onRemoveSection,
  isRemoveSectionLoading = false,
  onRestoreSection,
  isRestoreSectionLoading = false,
  restoreSectionDisabled = false,
  restoreSectionDisabledTitle = '',
  onRemoveItem,
  removingItemIds,
  onRestoreItem,
  restoringItemIds,
  removedOverlayItemIds = new Set(),
  dragHandleNode,
  reorderEnabled = false,
  onReorderItems,
}: Props) {
  const { t } = useTranslation();
  const removingItemIdSet = removingItemIds ?? NO_IDS_LOADING;
  const restoringItemIdSet = restoringItemIds ?? NO_IDS_LOADING;

  const itemSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const itemsReorderEnabled = Boolean(
    reorderEnabled && onReorderItems && !isSectionRemovedPresentation
  );

  const itemCount = section.items.length;
  const items = [...section.items].sort((a, b) => a.order - b.order);

  const removeSectionDisabled =
    sectionRemoveDisabled || isRemoveSectionLoading || isSectionRemovedPresentation;

  type ItemsIncludedInterpolation = {
    count: number;
    included: number;
    total: number;
  };
  const itemsIncludedValues: ItemsIncludedInterpolation = isSectionRemovedPresentation
    ? { count: 0, included: 0, total: 0 }
    : {
        count: itemCount,
        total: itemCount,
        included: section.items.filter(
          item => item.removed !== true && !removedOverlayItemIds.has(item.id)
        ).length,
      };

  const removeSectionTitle = sectionRemoveDisabled
    ? removeSectionDisabledTitle
    : '';

  const sectionHeaderRowAction = renderSectionHeaderRowAction({
    t,
    isSectionRemovedPresentation,
    onRestoreSection,
    isRestoreSectionLoading,
    restoreSectionDisabled,
    restoreSectionDisabledTitle,
    isRemoveSectionLoading,
    removeSectionDisabled,
    onRemoveSection,
    removeSectionTitle,
  });

  const buildItemProps = (item: typeof items[number]) => ({
    item,
    removeItemDisabledTitle,
    isRemovedPresentation:
      isSectionRemovedPresentation ||
      item.removed === true ||
      removedOverlayItemIds.has(item.id),
    onRemoveItem:
      isSectionRemovedPresentation || !onRemoveItem
        ? undefined
        : () => onRemoveItem(item.id),
    isRemoveLoading: removingItemIdSet.has(item.id),
    onRestoreItem:
      isSectionRemovedPresentation || !onRestoreItem
        ? undefined
        : () => onRestoreItem(item.id),
    isRestoreLoading: restoringItemIdSet.has(item.id),
  });

  const itemElements = items.map(item => {
    const itemProps = buildItemProps(item);

    if (!itemsReorderEnabled) {
      return <Item key={item.id} {...itemProps} />;
    }

    return (
      <SortableSectionCardItem
        key={item.id}
        {...itemProps}
        reorderEnabled
      />
    );
  });

  const handleItemDragEnd = (event: DragEndEvent) => {
    if (!onReorderItems) {
      return;
    }

    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    onReorderItems(String(active.id), String(over.id));
  };

  const itemsList = itemsReorderEnabled ? (
    <DndContext
      sensors={itemSensors}
      collisionDetection={closestCenter}
      onDragEnd={handleItemDragEnd}
    >
      <SortableContext
        items={items.map(item => item.id)}
        strategy={verticalListSortingStrategy}
      >
        {itemElements}
      </SortableContext>
    </DndContext>
  ) : (
    itemElements
  );

  return (
    <div id={`section-${section.id}`} className={styles.sectionCard}>
      <div
        className={classNames(styles.sectionHeaderRow, {
          [styles.sectionHeaderRowRemoved]: isSectionRemovedPresentation,
        })}
      >
        {dragHandleNode}
        <button
          type="button"
          className={classNames(styles.sectionToggle, {
            [styles.sectionToggleRemoved]: isSectionRemovedPresentation,
          })}
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <span className={styles.rowLead} aria-hidden>
            <Icon
              name={expanded ? 'chevron-down' : 'chevron-right'}
              set={ICON_SET_FONTAWESOME}
              type="fas"
              className={styles.sectionChevron}
            />
          </span>
          <span className={styles.sectionBody}>
            <span
              className={classNames(styles.sectionTitle, {
                [styles.sectionTitleRemoved]: isSectionRemovedPresentation,
              })}
            >
              {formatSectionTitle(section.name)}
            </span>
            <S
              variant="secondary"
              className={classNames(styles.sectionItemsIncluded, {
                [styles.sectionItemsIncludedRemoved]:
                  isSectionRemovedPresentation,
              })}
            >
              {t(
                'web.admin.inspections.templateCustomize.itemsIncluded',
                itemsIncludedValues
              )}
            </S>
          </span>
        </button>
        <div className={styles.rowActionCell}>{sectionHeaderRowAction}</div>
      </div>
      {expanded && itemsList}
    </div>
  );
}
